import { ArrowRight, Menu, X } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

import logoUrl from '@/assets/sancus-logo.svg';

export const sections = [
  ['Recursos', '#recursos'],
  ['Prazos', '#prazos'],
  ['Segurança', '#seguranca'],
  ['Dúvidas', '#duvidas'],
] as const;

export function LandingHeader() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const menuButton = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    const update = () => setScrolled(window.scrollY > 8);
    update();
    window.addEventListener('scroll', update, { passive: true });
    return () => window.removeEventListener('scroll', update);
  }, []);

  useEffect(() => {
    if (!menuOpen) return;

    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      setMenuOpen(false);
      menuButton.current?.focus();
    };

    document.addEventListener('keydown', closeOnEscape);
    return () => document.removeEventListener('keydown', closeOnEscape);
  }, [menuOpen]);

  const solid = scrolled || menuOpen;

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 transition-[background-color,box-shadow] duration-300 ${solid ? 'bg-white/85 shadow-[0_1px_0_var(--lp-line)] backdrop-blur-md' : ''}`}
    >
      <div className="mx-auto flex h-[72px] max-w-[1180px] items-center gap-6 px-5 sm:px-6">
        <a href="/" className="shrink-0" aria-label="Sancus — início">
          <img
            src={logoUrl}
            width="280"
            height="100"
            alt=""
            className={`h-9 w-auto transition-[filter]`}
          />
        </a>

        <nav
          aria-label="Navegação principal"
          className={`border-(--lp-line) text-(--lp-body) mx-auto hidden items-center gap-1 rounded-full border bg-white/80 p-1 text-[14.5px] transition-colors lg:flex`}
        >
          {sections.map(([label, href]) => (
            <a
              key={href}
              href={href}
              className={`hover:bg-(--lp-soft) hover:text-(--lp-ink) rounded-full px-4 py-1.5 transition-colors`}
            >
              {label}
            </a>
          ))}
        </nav>

        <div className="ml-auto flex items-center gap-2 lg:ml-0">
          <a
            href="/entrar"
            className={`text-(--lp-ink) hover:bg-(--lp-soft) hidden h-10 items-center rounded-xl px-4 text-[14.5px] font-medium transition-colors sm:inline-flex`}
          >
            Entrar
          </a>
          <a
            href="/criar-conta"
            className={`bg-primary hidden h-10 items-center rounded-xl px-4 text-[14.5px] font-medium text-white shadow-[inset_0_1px_0_rgb(255_255_255/0.18)] transition-colors sm:inline-flex`}
          >
            Criar conta gratuita
          </a>
          <button
            ref={menuButton}
            type="button"
            onClick={() => setMenuOpen(!menuOpen)}
            aria-expanded={menuOpen}
            aria-controls="lp-mobile-nav"
            aria-label={menuOpen ? 'Fechar menu' : 'Abrir menu'}
            className="border-(--lp-line) text-(--lp-ink) inline-flex size-10 items-center justify-center rounded-xl border bg-white lg:hidden"
          >
            {menuOpen ? <X size={19} /> : <Menu size={19} />}
          </button>
        </div>
      </div>

      {menuOpen && (
        <nav
          id="lp-mobile-nav"
          aria-label="Navegação móvel"
          className="border-(--lp-line) border-t bg-white px-5 pb-5 pt-2 lg:hidden"
        >
          {sections.map(([label, href]) => (
            <a
              key={href}
              href={href}
              onClick={() => setMenuOpen(false)}
              className="border-(--lp-line) text-(--lp-ink) block border-b py-3.5 text-base font-medium"
            >
              {label}
            </a>
          ))}
          <div className="mt-5 grid grid-cols-2 gap-2">
            <a
              href="/entrar"
              className="border-(--lp-line) text-(--lp-ink) inline-flex h-11 items-center justify-center rounded-xl border font-medium"
            >
              Entrar
            </a>
            <a
              href="/criar-conta"
              className="bg-primary inline-flex h-11 items-center justify-center gap-2 rounded-xl font-medium text-white"
            >
              Criar conta <ArrowRight size={16} />
            </a>
          </div>
        </nav>
      )}
    </header>
  );
}
